/**
 * Every date and time conversion in the app. The rules, and which kind of time each value is,
 * are in docs/claude/time-rules.md.
 */

/** The app's calendar: game days, "today", crons and season boundaries are all Eastern. */
export const EASTERN_TIME_ZONE = 'America/New_York';

/** A calendar day, `YYYY-MM-DD`. Not a moment: it has no zone of its own. */
export type CalendarDay = string;

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

export function isCalendarDay(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const easternFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function toInstant(value: Date | string): Date {
  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime()))
    throw new RangeError(`Not a valid instant: ${String(value)}`);
  return instant;
}

function easternWallClock(instant: Date): WallClock {
  const parts: Partial<Record<Intl.DateTimeFormatPartTypes, number>> = {};
  for (const part of easternFormatter.formatToParts(instant)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  return {
    year: parts.year ?? Number.NaN,
    month: parts.month ?? Number.NaN,
    day: parts.day ?? Number.NaN,
    hour: parts.hour ?? Number.NaN,
    minute: parts.minute ?? Number.NaN,
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function dayFromParts(year: number, month: number, day: number): CalendarDay {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function splitDay(day: CalendarDay): [number, number, number] {
  if (!isCalendarDay(day)) throw new RangeError(`Not a calendar day: ${day}`);
  const [year, month, date] = day.split('-').map(Number);
  return [year, month, date];
}

/**
 * The Eastern calendar day of a moment. A value that is already a calendar day comes back
 * unchanged, because `new Date('2026-01-15')` is UTC midnight, 7pm Eastern the day before.
 */
export function easternDay(value: Date | string): CalendarDay {
  if (typeof value === 'string' && isCalendarDay(value)) return value;
  const { year, month, day } = easternWallClock(toInstant(value));
  return dayFromParts(year, month, day);
}

/** Today's Eastern date: the app day. */
export function todayEastern(now: Date = new Date()): CalendarDay {
  return easternDay(now);
}

/** The UTC calendar day of a moment, for what is filed by UTC day (log and archive files). */
export function utcDay(value: Date | string): CalendarDay {
  if (typeof value === 'string' && isCalendarDay(value)) return value;
  const instant = toInstant(value);
  return dayFromParts(instant.getUTCFullYear(), instant.getUTCMonth() + 1, instant.getUTCDate());
}

/** The calendar day of a moment on this device's clock, for grouping what a user sees. */
export function deviceDay(value: Date): CalendarDay {
  const instant = toInstant(value);
  return dayFromParts(instant.getFullYear(), instant.getMonth() + 1, instant.getDate());
}

/** `YYYYMMDD`, the form ESPN, MySportsFeeds and Roster Intel take. */
export function compactDay(day: CalendarDay): string {
  splitDay(day);
  return day.replace(/-/g, '');
}

/** ISO 8601 to the second, the form the Odds API historical endpoints take: they refuse milliseconds. */
export function isoToTheSecond(at: Date): string {
  return at.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** Calendar arithmetic: `n` days after `day` (negative for before), with no clock involved. */
export function addDays(day: CalendarDay, n: number): CalendarDay {
  const [year, month, date] = splitDay(day);
  return utcDay(new Date(Date.UTC(year, month - 1, date + n)));
}

/** Whole calendar days from `from` to `to`, negative when `to` is earlier. */
export function daysBetween(from: CalendarDay, to: CalendarDay): number {
  const [y1, m1, d1] = splitDay(from);
  const [y2, m2, d2] = splitDay(to);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / MS_PER_DAY);
}

/**
 * How a game day is stored in a timestamptz column: noon UTC on that day, which every US zone
 * reads back as the same date.
 */
export function gameDayTimestamp(day: CalendarDay): Date {
  splitDay(day);
  return new Date(`${day}T12:00:00.000Z`);
}

/** The stored game day of a game that tips at `tip`. */
export function gameDayTimestampForTip(tip: Date | string): Date {
  return gameDayTimestamp(easternDay(tip));
}

/** How far the Eastern wall clock is from UTC at `instant`, in minutes: -240 (EDT) or -300 (EST). */
export function easternOffsetMinutes(instant: Date): number {
  const minute = Math.floor(toInstant(instant).getTime() / MS_PER_MINUTE) * MS_PER_MINUTE;
  const wall = easternWallClock(new Date(minute));
  const wallAsUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  return (wallAsUtc - minute) / MS_PER_MINUTE;
}

/**
 * The moment the Eastern wall clock reads `hour:minute:second` on `day`, at that time's own
 * offset. A time the spring change skips (2:30am) lands an hour later, as clocks do; a time the
 * fall change repeats (1:30am) is the first of the two.
 */
export function easternDateTime(day: CalendarDay, hour: number, minute = 0, second = 0): Date {
  const [year, month, date] = splitDay(day);
  const wallAsUtc = Date.UTC(year, month - 1, date, hour, minute, second);
  const offsets = [
    easternOffsetMinutes(new Date(wallAsUtc - MS_PER_DAY)),
    easternOffsetMinutes(new Date(wallAsUtc + MS_PER_DAY)),
  ];
  const candidates = offsets
    .map((offset) => wallAsUtc - offset * MS_PER_MINUTE)
    .sort((a, b) => a - b);
  const wallMinute = Math.floor(wallAsUtc / MS_PER_MINUTE) * MS_PER_MINUTE;
  const matching = candidates.find((t) => {
    const wall = easternWallClock(new Date(t));
    return Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute) === wallMinute;
  });
  return new Date(matching ?? candidates[candidates.length - 1]);
}

/** Midnight Eastern at the start of `day`. */
export function startOfEasternDay(day: CalendarDay): Date {
  return easternDateTime(day, 0);
}

/** 23:59:59 Eastern on `day`, at that evening's offset. */
export function endOfEasternDay(day: CalendarDay): Date {
  return easternDateTime(day, 23, 59, 59);
}

/** The Eastern wall-clock hour, 0 to 23. */
export function easternHour(instant: Date = new Date()): number {
  return easternWallClock(toInstant(instant)).hour;
}

/** Minutes since Eastern midnight, 0 to 1439: a 1:00 PM ET tip is 780. */
export function easternMinutesSinceMidnight(instant: Date | string): number {
  const { hour, minute } = easternWallClock(toInstant(instant));
  return hour * 60 + minute;
}

/** Milliseconds from `now` until the Eastern wall clock next reads `hour:minute`. */
export function msUntilNextEastern(hour: number, minute = 0, now: Date = new Date()): number {
  const today = todayEastern(now);
  let target = easternDateTime(today, hour, minute);
  if (target.getTime() <= now.getTime()) target = easternDateTime(addDays(today, 1), hour, minute);
  return target.getTime() - now.getTime();
}

/** `MM-DD` of the Eastern date, the form season boundaries are written in. */
export function easternMonthDay(instant: Date = new Date()): string {
  return easternDay(instant).slice(5);
}

/** Calendar year and month (1 to 12) of the Eastern date. */
export function easternYearMonth(instant: Date = new Date()): { year: number; month: number } {
  const [year, month] = splitDay(easternDay(instant));
  return { year, month };
}

/** "Monday" for a calendar day, or for the Eastern day of a moment. */
export function weekdayName(value: Date | CalendarDay): string {
  return formatCalendarDay(easternDay(value), { weekday: 'long' });
}

/**
 * Format a calendar day for display without letting a zone move it: Jan 14 shows as Jan 14 on
 * every device.
 */
export function formatCalendarDay(
  day: CalendarDay,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US',
): string {
  return gameDayTimestamp(day).toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
}

/** Format a moment on the Eastern clock, for text that names a time in ET. */
export function formatEastern(
  instant: Date | string,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US',
): string {
  return toInstant(instant).toLocaleString(locale, { ...options, timeZone: EASTERN_TIME_ZONE });
}

/** An Eastern clock time for a log line, such as a game's tip: "8:30 PM EDT". */
export function formatEasternClock(instant: Date | string): string {
  return formatEastern(instant, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
}

/** An Eastern date and time for a log line, such as when a run started: "11/1/2026, 10:44:31 PM EST". */
export function formatEasternStamp(instant: Date | string): string {
  return formatEastern(instant, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}
